import React, { useEffect, useState, useCallback } from 'react';
import { Boxes, Upload, Plus } from 'lucide-react';
import opsService from '../../../services/opsService';

function Card({ label, value, sub }) {
  return (
    <div className="bg-surface-card rounded-card shadow-elevation-1 border p-4">
      <div className="text-ink-muted text-small">{label}</div>
      <div className="text-2xl font-bold text-ink-soft">{value}</div>
      {sub && <div className="text-ink-muted text-small mt-1">{sub}</div>}
    </div>
  );
}

const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function OpsInventory() {
  const [summary, setSummary] = useState(null);
  const [items, setItems] = useState([]);
  const [q, setQ] = useState('');
  const [lowOnly, setLowOnly] = useState(false);
  const [msg, setMsg] = useState(null);

  const load = useCallback(async () => {
    const [s, list] = await Promise.all([
      opsService.inventorySummary().catch(() => null),
      opsService.listInventory(q, lowOnly).catch(() => []),
    ]);
    setSummary(s); setItems(list);
  }, [q, lowOnly]);

  useEffect(() => { load(); }, [load]);

  const onCsv = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setMsg('Importing…');
    try {
      const r = await opsService.importInventoryCsv(file);
      setMsg(`Imported ${r.imported}, updated ${r.updated}, skipped ${r.skipped}.`);
      load();
    } catch (err) {
      setMsg(err?.response?.data?.detail || 'Import failed');
    } finally {
      e.target.value = '';
    }
  };

  const addItem = async () => {
    const name = window.prompt('Item name?');
    if (!name) return;
    const part_number = window.prompt('Part number? (optional)') || null;
    const quantity_on_hand = Number(window.prompt('Opening quantity?', '0') || 0);
    const reorder_level = Number(window.prompt('Reorder level?', '0') || 0);
    try { await opsService.createInventoryItem({ name, part_number, quantity_on_hand, reorder_level }); load(); }
    catch (err) { setMsg(err?.response?.data?.detail || 'Create failed'); }
  };

  const adjust = async (it) => {
    const v = window.prompt(`New on-hand count for "${it.name}" (current ${it.quantity_on_hand})?`, String(it.quantity_on_hand));
    if (v == null) return;
    const note = window.prompt('Note (reason)?', 'manual adjustment') || 'manual adjustment';
    try { await opsService.adjustInventory(it.id, { new_quantity: Number(v), note }); load(); }
    catch (err) { setMsg(err?.response?.data?.detail || 'Adjust failed'); }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-bold text-ink-soft flex items-center gap-2">
          <Boxes className="w-5 h-5" /> Inventory
        </h3>
        <div className="flex items-center gap-2">
          <button onClick={addItem} className="flex items-center gap-1 px-3 py-2 text-small border rounded-lg hover:bg-surface-muted">
            <Plus className="w-4 h-4" /> Add item
          </button>
          <label className="flex items-center gap-1 px-3 py-2 text-small border rounded-lg hover:bg-surface-muted cursor-pointer">
            <Upload className="w-4 h-4" /> Import CSV
            <input type="file" accept=".csv" className="hidden" onChange={onCsv} />
          </label>
        </div>
      </div>

      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Card label="Items" value={summary.total_items} sub={`${summary.active_items} active`} />
          <Card label="Stock value" value={money(summary.total_stock_value)} />
          <Card label="Low stock" value={summary.low_stock_count} />
          <Card label="Out of stock" value={summary.out_of_stock_count} />
        </div>
      )}

      {msg && <div className="text-small text-ink-muted">{msg}</div>}

      <div className="flex items-center gap-3">
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name / part # / SKU"
               className="px-3 py-2 border rounded-lg text-small flex-1 max-w-sm border-border-strong" />
        <label className="flex items-center gap-1 text-small text-ink-muted">
          <input type="checkbox" checked={lowOnly} onChange={(e) => setLowOnly(e.target.checked)} /> Low stock only
        </label>
      </div>

      <div className="bg-surface-card rounded-card shadow-elevation-1 border overflow-hidden">
        <table className="w-full text-small">
          <thead className="bg-surface-muted text-ink-muted">
            <tr>
              <th className="text-left px-4 py-2">Item</th><th className="text-left px-4 py-2">Part #</th>
              <th className="text-right px-4 py-2">On hand</th><th className="text-right px-4 py-2">Reorder</th>
              <th className="text-left px-4 py-2">Bin</th><th className="text-right px-4 py-2">Value</th>
              <th className="px-4 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {items.map((it) => (
              <tr key={it.id} className={`border-t ${it.is_low ? 'bg-amber-50' : ''}`}>
                <td className="px-4 py-2 text-ink-soft">{it.name}{it.is_low && <span className="ml-2 text-amber-700 text-small">low</span>}</td>
                <td className="px-4 py-2">{it.part_number || '—'}</td>
                <td className="px-4 py-2 text-right">{it.quantity_on_hand}</td>
                <td className="px-4 py-2 text-right">{it.reorder_level}</td>
                <td className="px-4 py-2">{it.bin_location || '—'}</td>
                <td className="px-4 py-2 text-right">{money(it.stock_value)}</td>
                <td className="px-4 py-2 text-right">
                  <button className="text-accent hover:underline" onClick={() => adjust(it)}>Adjust</button>
                </td>
              </tr>
            ))}
            {items.length === 0 && (
              <tr><td colSpan="7" className="px-4 py-6 text-center text-ink-muted">No inventory yet — import a CSV or add an item.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
